import { Link } from "react-router-dom";
import { caseStudies } from "@/mocks/homeData";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function CaseStudiesSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="py-20 lg:py-28 bg-[#f9f7f4]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">
              Proven Results
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight">
              Real-World Impact: How Our <br />
              <strong>Solutions Make a Difference</strong>
            </h2>
          </div>
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-[#1a1a1a] transition-colors whitespace-nowrap cursor-pointer"
          >
            Read All Case Studies
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Case Studies Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {caseStudies.map((cs, i) => (
            <Link
              key={cs.title}
              to="/case-studies"
              className={`group relative bg-white rounded-lg overflow-hidden border border-gray-100 cursor-pointer transition-all ease-out hover:border-transparent hover:-translate-y-1 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{
                transitionDuration: "500ms",
                transitionDelay: cardsVisible ? `${i * 120}ms` : "0ms",
              }}
            >
              {/* Top accent bar — red on hover */}
              <div className="h-1 bg-[#1a1a1a] group-hover:bg-[#e63b2e] transition-colors duration-400" />

              {/* Accent line left — grows on hover */}
              <div className="absolute left-0 top-1 bottom-0 w-[3px] bg-[#e63b2e] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out origin-bottom" />

              <div className="p-6">
                {/* Meta row */}
                <div className="flex items-center justify-between mb-5">
                  <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 group-hover:text-[#e63b2e] transition-colors duration-300">
                    {cs.industry}
                  </span>
                  <span className="text-xs bg-[#1a1a1a]/5 group-hover:bg-[#1a1a1a] group-hover:text-white text-[#1a1a1a] px-2.5 py-1 rounded-full font-medium transition-all duration-300">
                    {cs.brand}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-bold text-[#1a1a1a] text-base mb-3 leading-snug group-hover:text-[#1a1a1a] transition-colors duration-300">
                  {cs.title}
                </h3>

                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{cs.description}</p>

                {/* Result block */}
                <div className="bg-[#f9f7f4] group-hover:bg-[#1a1a1a] rounded-md p-3 flex items-center gap-3 transition-colors duration-400">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] group-hover:bg-[#e63b2e] rounded-md flex-shrink-0 transition-colors duration-400">
                    <i className="ri-bar-chart-line text-white text-sm" />
                  </div>
                  <p className="text-sm font-semibold text-[#1a1a1a] group-hover:text-white transition-colors duration-400">
                    {cs.result}
                  </p>
                </div>

                {/* Read more — slides up on hover */}
                <div className="flex items-center gap-1.5 mt-4 text-[#e63b2e] text-xs font-semibold translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-350 ease-out">
                  <span>Read Full Case Study</span>
                  <i className="ri-arrow-right-line text-xs transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
